import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  InputAdornment,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import i18next from "i18next";
import { baseLightColors } from "@rentablo/theming";
import { BankWrapper } from "@rentablo/api/apiv1";
import { useBanks } from "@rentablo/api/apiv1";
import SearchIcon from "@icons/myicons/search.svg?react";
import { StyledListItem } from "@features/accounts/edit/components/CreateAndEditAccountForm";
import { SecurityImage } from "@rentablo/components";
import banksList from "@features/bankSelect/components/BankSelect/banksList";

type BankOption = BankWrapper & { __freeText?: boolean };

const renderOption = (theme: Theme) => (props: Object, option: BankOption) => {
  if (option.__freeText) {
    return (
      <li {...props} key="__free-text-slug">
        <StyledListItem
          id="bank-option-free-text"
          data-testid="bank-option-free-text"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="24px"
              height="24px"
              color="icon.secondary"
            >
              <SearchIcon width="24px" height="24px" />
            </Box>
            <Typography variant="caption" color="textPrimary">
              <Trans
                i18nKey="components.bankSearchField.useSlug"
                values={{ value: option.slug }}
              />
            </Typography>
          </Box>
        </StyledListItem>
      </li>
    );
  }

  return (
    <li {...props} key={option.bic}>
      <StyledListItem
        id={`bank-option-${option.id}`}
        data-testid={`bank-option-${option.bic}`}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            display="flex"
            alignItems="center"
            width="24px"
            height="24px"
            sx={{
              border: `1px solid ${theme.palette.border.primary}`,
              borderRadius: `4px`,
              backgroundColor: theme.palette.common.white,
              overflow: "hidden",
            }}
          >
            <img
              src={option.iconUrl}
              width="100%"
              alt="bank-logo"
              style={{
                objectFit: "contain",
              }}
            />
          </Box>
          <Typography variant="caption" color="textPrimary">
            {option.name}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textPrimary">
            {option.bic}
          </Typography>
        </Box>
      </StyledListItem>
    </li>
  );
};

const renderSearchInput =
  (bank?: BankWrapper | null, title?: string, placeholder?: string) =>
  (params: AutocompleteRenderInputParams) => (
    <Box position="relative">
      <TextField
        {...params}
        data-testid="bank-search"
        label={
          i18next.t(
            title ? title : "components.bankSearchField.label"
          ) as string
        }
        placeholder={i18next.t(
          placeholder ? placeholder : "components.bankSearchField.placeholder"
        )}
        sx={{
          "& .MuiInputBase-input": {
            paddingLeft: `2px !important`,
          },
        }}
        slotProps={{
          input: {
            ...params.InputProps,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  marginLeft: (theme) => theme.spacing(3),
                }}
              >
                {bank?.iconUrl ? (
                  <SecurityImage
                    src={bank?.iconUrl || ""}
                    width={20}
                    height={20}
                  />
                ) : (
                  <Box color="icon.secondary" height="24px">
                    <SearchIcon width="24px" height="24px" />
                  </Box>
                )}
              </InputAdornment>
            ),
          },
          htmlInput: {
            ...params.inputProps,
            sx: {
              "&::placeholder": {
                color: "text.secondary",
                opacity: 1,
              },
            },
            "data-testid": "bank-search-input",
          },
        }}
      />
    </Box>
  );

const ListboxComponent = function ListboxComponent({
  ref,
  ...props
}: any & { ref?: React.Ref<HTMLUListElement> }) {
  const { t } = useTranslation();

  return (
    <>
      <Box display="flex" justifyContent="space-between" px={4} pt={2}>
        <Typography
          variant="overline"
          color={baseLightColors.N400}
          fontWeight={600}
        >
          {t("accountTypes.bankSelect.bankName")}
        </Typography>
        <Typography
          variant="overline"
          color={baseLightColors.N400}
          fontWeight={600}
        >
          {t("accountTypes.bankSelect.bic")}
        </Typography>
      </Box>
      <ul ref={ref} {...props} style={{ height: "33vh" }} />
    </>
  );
};

type Props = {
  title?: string;
  placeholder?: string;
  defaultBank?: BankWrapper | null;
  onBankClick?: (bank: BankWrapper | null) => void;
  allowFreeTextSlug?: boolean;
  /**
   * Invoked when the user selects the synthetic `Search "<typed>"` row in the
   * dropdown — only reachable when `allowFreeTextSlug` is set. Receives the
   * trimmed typed text. Free-text picks never flow through `onBankClick`.
   */
  onFreeTextSlug?: (slug: string) => void;
};

export const BankSearchField = ({
  title,
  placeholder,
  defaultBank,
  onBankClick,
  allowFreeTextSlug = false,
  onFreeTextSlug,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = React.useState<BankOption[]>([]);
  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState<BankOption | null>(
    defaultBank || null
  );

  const handleBankClick = (value: BankOption | null) => {
    setSelectedBank(value);
    if (value?.__freeText) {
      onFreeTextSlug?.(value.slug);
      return;
    }
    onBankClick?.(value);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    defaultBank && setSelectedBank(defaultBank);
  }, [defaultBank]);

  useBanks(
    { search: debouncedSearchValue },
    {
      enabled: Boolean(debouncedSearchValue),
      onSuccess: (data) => {
        setOptions(data?.banks ?? []);
        setLoading(false);
      },
    }
  );
  useEffect(() => {
    if (!debouncedSearchValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptions(banksList as unknown as BankOption[]);
    }
  }, [debouncedSearchValue]);

  const trimmedSearch = searchValue.trim();
  const displayOptions: BankOption[] =
    allowFreeTextSlug && trimmedSearch
      ? [
          {
            __freeText: true,
            slug: trimmedSearch,
            name: trimmedSearch,
          } as BankOption,
          ...options,
        ]
      : options;

  return (
    <Autocomplete
      id="bank-search"
      data-testid="bank-autocomplete"
      value={selectedBank}
      open={open}
      options={displayOptions}
      loading={loading}
      disablePortal
      renderOption={renderOption(theme)}
      renderInput={renderSearchInput(selectedBank, title, placeholder)}
      ListboxComponent={ListboxComponent}
      inputValue={searchValue}
      getOptionLabel={(option) => (option as BankOption).name}
      filterOptions={(options) => options}
      blurOnSelect
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      noOptionsText={t("accountTypes.bankSelect.noOptions")}
      onChange={(_, value) => handleBankClick((value as BankOption) || null)}
      onInputChange={(_, value: string) => {
        setLoading(!!value);
        setSearchValue(value);
      }}
    />
  );
};
